import {
  createRootRoute,
  createRoute,
  createRouter,
  Link,
  Outlet,
  redirect,
} from '@tanstack/react-router'
import { AppShell } from '@app/components/app-shell'
import { Button } from '@app/components/ui/button'
import { sessionQuery } from '@app/features/auth/api'
import type { CariFilter } from '@app/features/cari/api'
import type { ModelSearchMode, ModelSortOrder } from '@app/features/modeller/api'
import type { OrderFilter, OrderSort, OrderType } from '@app/features/orders/types'
import type { PurchaseFilter } from '@app/features/satinalma/api'
import type { StockSort } from '@app/features/stok/api'
import type { StatusFilter } from '@app/features/uretim/production-api'
import { MAX_QUERY_LENGTH } from '@app/lib/nav'
import type { AppPath } from '@app/lib/nav'
import { queryClient } from '@app/lib/query-client'
import type { ChequeDirection } from '@app/features/finans/api'
import { CariScreen } from '@app/screens/cari'
import { CariDetailScreen } from '@app/screens/cari/cari-detail'
import { DashboardScreen } from '@app/screens/dashboard'
import { FinansScreen } from '@app/screens/finans'
import { FinansTahsilatScreen } from '@app/screens/finans/tahsilat'
import { LoginScreen } from '@app/screens/giris'
import { ModelsScreen } from '@app/screens/modeller'
import { ModelDetailScreen } from '@app/screens/modeller/model-detail'
import { SayaScreen } from '@app/screens/modeller/saya'
import { PurchasingScreen } from '@app/screens/satinalma'
import { PurchaseDetailScreen } from '@app/screens/satinalma/purchase-detail'
import { OrdersScreen } from '@app/screens/siparisler'
import { OrderDetailScreen } from '@app/screens/siparisler/order-detail'
import { StockScreen } from '@app/screens/stok'
import { StockCardScreen } from '@app/screens/stok/detail'
import { ProductionScreen } from '@app/screens/uretim'
import { EmirDetailScreen } from '@app/screens/uretim/emir-detail'
import { guardedAreaForAppPath } from '../worker/api/auth.contract'

/**
 * Code-based route tree (no file-based generation), so routes stay explicit and
 * greppable. Add a route here and to `NAV` in lib/nav.ts.
 *
 * Two branches hang off the root: `/giris` (bare login screen) and the pathless
 * `_korumali` layout, which owns the AppShell and refuses to load without a
 * session. The layout id is part of every child route id, so screens address
 * themselves as `from: '/_korumali/stok/$skod'` — which is why the role check on
 * the guarded routes is a per-route `beforeLoad` (see `requireAccessTo`) rather
 * than another layout: a second layout would rename their ids and every
 * `getRouteApi`/`useParams` string with them, for one shared line.
 */

const rootRoute = createRootRoute({
  component: () => <Outlet />,
  notFoundComponent: NotFound,
})

/* ── List-screen URL state ───────────────────────────────────────────────────
 *
 * Search, filters, sort and page live in the address bar on every list
 * route, so Back, refresh and a bookmarked link all land on the same rows.
 * Two rules keep the URLs readable:
 *
 *   1. Param names match the API vocabulary (`query`, `status`, `type`, `tip`,
 *      `sort`, `page`, `location`, `onlyCritical`, `mode`) — what you read in
 *      the address bar is what the request carries.
 *   2. A default is written as `undefined`, and TanStack's serializer drops
 *      undefined keys, so an untouched screen has a bare URL (`/siparisler`)
 *      instead of `?query=&status=all&page=1`. Screens apply the defaults on
 *      read.
 *
 * Everything here is defensive on purpose: the search string is user input,
 * and an unknown sort value must degrade to the default rather than reach the
 * API or crash the screen.
 */

// MAX_QUERY_LENGTH lives in lib/nav.ts so the search-input hook caps what it
// commits at the same length this file keeps from the URL — two different
// caps would leave the box and the address bar permanently disagreeing about
// text past the limit.

/**
 * Free text. Numbers come back from the parser as numbers ("?query=86845"), so
 * they are coerced back to text instead of being dropped.
 */
function textParam(value: unknown): string | undefined {
  const raw =
    typeof value === 'string' ? value : typeof value === 'number' ? String(value) : ''
  const trimmed = raw.trim().slice(0, MAX_QUERY_LENGTH)
  return trimmed === '' ? undefined : trimmed
}

/** Enum param: anything unknown collapses to the screen's default (undefined). */
function enumParam<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return allowed.includes(value as T) ? (value as T) : undefined
}

/** Numeric enum (the date presets); anything else collapses to the default. */
function numberParam<T extends number>(value: unknown, allowed: readonly T[]): T | undefined {
  const n = typeof value === 'number' ? value : Number(value)
  return allowed.includes(n as T) ? (n as T) : undefined
}

/** 1-based page number; page 1 is the default and stays out of the URL. */
function pageParam(value: unknown): number | undefined {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isInteger(n) && n > 1 && n <= 10_000 ? n : undefined
}

/** Boolean toggle; only `true` is ever written. */
function flagParam(value: unknown): true | undefined {
  return value === true || value === 'true' ? true : undefined
}

const ORDER_FILTERS: readonly OrderFilter[] = [
  'all',
  'open',
  'overdue',
  'pending',
  'partial',
  'complete',
  'closed',
]
/**
 * Document-type filter.
 *
 * `sales` is not a Korgun `SipTip` — it is the API's shorthand for
 * `SipTip IN ('S','ST')`, sales orders plus samples. It is the screen's
 * DEFAULT (and therefore absent from the URL): purchase orders have had their
 * own module since satınalma shipped, so showing them here as well double-
 * counted the order book. `type=A` still brings them back.
 */
export type OrderTypeParam = OrderType | 'all' | 'sales'
/** `sales` is missing on purpose: it is the default, so it never reaches the URL. */
const ORDER_TYPES: readonly OrderTypeParam[] = ['all', 'S', 'A', 'ST']
const ORDER_SORTS: readonly OrderSort[] = ['priority', 'date', 'dueDate', 'amount']
/** Relative date presets, in days — kept relative so a bookmark stays "son 30 gün". */
const ORDER_DAY_PRESETS = [30, 90, 365] as const

export interface OrdersSearch {
  query?: string
  status?: OrderFilter
  /** Absent = the `sales` default (satış + numune). */
  type?: OrderTypeParam
  /** Rolling window in days; absent = all dates. */
  days?: (typeof ORDER_DAY_PRESETS)[number]
  sort?: OrderSort
  page?: number
}

const PURCHASE_FILTERS: readonly PurchaseFilter[] = [
  'all',
  'open',
  'overdue',
  'pending',
  'partial',
  'complete',
  'closed',
]

export interface PurchasingSearch {
  query?: string
  status?: PurchaseFilter
  /** Cari code of the supplier tile that drove the filter, for its active state. */
  supplier?: string
  page?: number
}

const STOCK_SORTS: readonly StockSort[] = ['stock', 'code', 'quantity']

export interface StockSearch {
  query?: string
  location?: string
  onlyCritical?: true
  sort?: StockSort
  page?: number
}

/**
 * `other` = the 335/360 accounts (payroll and the rest). The list screen shows
 * them as their own chip, so the count next to "Diğer" is reachable instead of
 * being a dead-end note. Korgun's own prefix convention decides the bucket —
 * see `TYPE_SQL` in the cari API.
 */
export type CariTipParam = CariFilter | 'other'
const CARI_FILTERS: readonly CariTipParam[] = ['all', 'customer', 'supplier', 'other']

export interface CariSearch {
  query?: string
  tip?: CariTipParam
  page?: number
}

const PRODUCTION_FILTERS: readonly StatusFilter[] = ['all', 'planned', 'inProgress', 'done']

export interface ProductionSearch {
  query?: string
  status?: StatusFilter
  /**
   * Korgun `Proses_M.Pro` code the emir list is narrowed to, set by the process
   * board tiles. Two characters ("04"), so it is carried as text — a numeric
   * parse would lose the leading zero.
   */
  proses?: string
  page?: number
}

const MODEL_MODES: readonly ModelSearchMode[] = ['model', 'material']
const MODEL_SORTS: readonly ModelSortOrder[] = ['lastUsed', 'code', 'color', 'bom']

export interface ModelsSearch {
  query?: string
  mode?: ModelSearchMode
  sort?: ModelSortOrder
  page?: number
}

/**
 * How the saya screen was entered. `scan` is the default and therefore never
 * written; `?source=dxf` is what the model card's "DXF içe aktar" button opens,
 * so a reload lands on the same half of the screen. It only picks the entry
 * affordance — a revision's real source is decided by what gets uploaded.
 */
const SAYA_SOURCES = ['dxf'] as const

export interface SayaSearch {
  source?: (typeof SAYA_SOURCES)[number]
}

/* ── End of list-screen URL state ───────────────────────────────────────────── */

/** Login — outside the shell, the only route reachable without a session. */
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/giris',
  component: LoginScreen,
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => {
    const target = search.redirect
    // In-app paths only — no open redirect.
    return typeof target === 'string' && target.startsWith('/') && !target.startsWith('//')
      ? { redirect: target }
      : {}
  },
})

/**
 * Everything below needs a session. The guard runs before any screen loader, so
 * an expired cookie never gets as far as rendering a half-empty shell.
 */
const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_korumali',
  component: AppShell,
  beforeLoad: async ({ location }) => {
    const user = await queryClient.ensureQueryData(sessionQuery)
    if (!user) {
      throw redirect({ to: '/giris', search: { redirect: location.href }, replace: true })
    }
  },
})

/**
 * The guarded modules (cari, satınalma, finans) carry a second guard on top of
 * the session one, on each of their five routes.
 *
 * The capability is looked up from the route's OWN path in `GUARDED_AREAS` (the
 * table the Worker's gate reads), not restated here: /cari therefore asks
 * `canViewCari` and /satinalma asks `canViewPurchasing` without this file
 * knowing that either predicate exists, and a route cannot end up guarded
 * against the wrong one. A path outside the table yields a guard that admits
 * everyone — which is exactly what "not a guarded area" means.
 *
 * It is a redirect to the panel, not a "yetkisiz" screen: a user reaching /cari
 * without the right means a stale bookmark or a shared link, and the useful
 * answer is the screen they do have rather than a dead end. Nothing here is
 * security — the Worker answers those endpoints with a 403 regardless
 * (worker/index.ts) and the panel payload arrives without the blocks behind
 * them. This only keeps a screen that could never load from being rendered.
 *
 * `ensureQueryData` reads the same cache entry the parent guard just filled, so
 * this costs no extra request. When there is no session at all the parent has
 * already thrown its redirect to /giris and this never runs.
 */
function requireAccessTo(path: AppPath): () => Promise<void> {
  const area = guardedAreaForAppPath(path)
  return async function guard(): Promise<void> {
    if (!area) return
    const user = await queryClient.ensureQueryData(sessionQuery)
    if (user && !area.can(user.role)) {
      throw redirect({ to: '/', replace: true })
    }
  }
}

const requirePurchasingAccess = requireAccessTo('/satinalma')
const requireCariAccess = requireAccessTo('/cari')
const requireFinanceAccess = requireAccessTo('/finans')

const dashboardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/',
  component: DashboardScreen,
})

const stockRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/stok',
  component: StockScreen,
  validateSearch: (search: Record<string, unknown>): StockSearch => ({
    query: textParam(search.query),
    // Warehouse codes are short opaque strings ("01", "MRK") — kept verbatim.
    location: textParam(search.location),
    onlyCritical: flagParam(search.onlyCritical),
    sort: enumParam(search.sort, STOCK_SORTS),
    page: pageParam(search.page),
  }),
})

/* ── Stock module: detail routes ────────────────────────────────────────────
 * Stock card opened from a list row: /stok/<SKod>. SKod may contain spaces and
 * dots, so the parameter is URL-encoded (see features/stok/api.ts).
 */
const stockCardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/stok/$skod',
  component: StockCardScreen,
})
/* ── End of Stock module detail routes ──────────────────────────────────────── */

const ordersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/siparisler',
  component: OrdersScreen,
  validateSearch: (search: Record<string, unknown>): OrdersSearch => ({
    query: textParam(search.query),
    status: enumParam(search.status, ORDER_FILTERS),
    type: enumParam(search.type, ORDER_TYPES),
    days: numberParam(search.days, ORDER_DAY_PRESETS),
    sort: enumParam(search.sort, ORDER_SORTS),
    page: pageParam(search.page),
  }),
})

/* ── Orders module: detail routes ─────────────────────────────────────────── */

/** /siparisler/$sipNo — Korgun Siparis_Kay.SipNo (integer, carried as text). */
const orderDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/siparisler/$sipNo',
  component: OrderDetailScreen,
})

/* ── End of Orders module ─────────────────────────────────────────────────── */

/* ── Purchasing module ──────────────────────────────────────────────────────
 * The owner-facing view of the SipTip='A' orders: what we ordered from which
 * supplier, what arrived, what is still due. Sales orders are unreachable from
 * these routes — /satinalma/<sales order no> is a 404, not a redirect.
 */

const purchasingRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/satinalma',
  component: PurchasingScreen,
  beforeLoad: requirePurchasingAccess,
  validateSearch: (search: Record<string, unknown>): PurchasingSearch => ({
    query: textParam(search.query),
    status: enumParam(search.status, PURCHASE_FILTERS),
    supplier: textParam(search.supplier),
    page: pageParam(search.page),
  }),
})

/** /satinalma/$sipNo — Korgun Siparis_Kay.SipNo (integer, carried as text). */
const purchasingDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/satinalma/$sipNo',
  component: PurchaseDetailScreen,
  beforeLoad: requirePurchasingAccess,
})

/* ── End of Purchasing module ─────────────────────────────────────────────── */

const cariRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/cari',
  component: CariScreen,
  beforeLoad: requireCariAccess,
  validateSearch: (search: Record<string, unknown>): CariSearch => ({
    query: textParam(search.query),
    tip: enumParam(search.tip, CARI_FILTERS),
    page: pageParam(search.page),
  }),
})

/* ── Cari module: detail routes ─────────────────────────────────────────────
 * "Cari" is kept as a domain noun (the Turkish accounting term for a
 * customer/supplier account) — there is no English label the users would
 * recognise on the sidebar.
 */

/**
 * /cari/$ckod — Korgun `Cari_Kart.CKod`. The code is dotted ("120.01.0253") and
 * sometimes space-separated ("120 01 0013"), so the parameter is URL-encoded
 * (see features/cari/api.ts).
 */
const cariDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/cari/$ckod',
  component: CariDetailScreen,
  beforeLoad: requireCariAccess,
})

/* ── End of Cari module ───────────────────────────────────────────────────── */

/* ── Finans module ──────────────────────────────────────────────────────────
 * The cheque book as a maturity calendar (+ the data-freshness strip).
 * Admin-only via the `finance` guarded area. `/finans` renders the Çek Takvimi
 * itself — one screen needs no hub; slice 2 adds `/finans/tahsilat` beside it.
 */

/** `issued` is the default and stays out of the URL, so only this is written. */
const FINANS_DIRECTIONS: readonly ChequeDirection[] = ['received']

interface FinansSearch {
  direction?: ChequeDirection
  query?: string
}

const finansRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/finans',
  component: FinansScreen,
  beforeLoad: requireFinanceAccess,
  validateSearch: (search: Record<string, unknown>): FinansSearch => ({
    direction: enumParam(search.direction, FINANS_DIRECTIONS),
    query: textParam(search.query),
  }),
})

/**
 * Dates travel as text; the API rejects a bad range with a Turkish 400, so
 * the coercion here only has to keep the URL tidy.
 */
interface FinansTahsilatSearch {
  from?: string
  to?: string
  /** Namespaced `raw:<CKod>` / `alias:<advisorKod4>` token. */
  customer?: string
  query?: string
}

/** SPA guards are per-route — a sibling inherits NOTHING from /finans. */
const finansTahsilatRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/finans/tahsilat',
  component: FinansTahsilatScreen,
  beforeLoad: requireFinanceAccess,
  validateSearch: (search: Record<string, unknown>): FinansTahsilatSearch => ({
    from: textParam(search.from),
    to: textParam(search.to),
    customer: textParam(search.customer),
    query: textParam(search.query),
  }),
})

/* ── End of Finans module ───────────────────────────────────────────────────── */

const productionRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/uretim',
  component: ProductionScreen,
  validateSearch: (search: Record<string, unknown>): ProductionSearch => ({
    query: textParam(search.query),
    status: enumParam(search.status, PRODUCTION_FILTERS),
    // Same validation as the API (production.ts): only '01'..'08' filters;
    // anything else is dropped so the UI can't show an active-but-inert pill.
    proses:
      typeof search.proses === 'string' && /^0[1-8]$/.test(search.proses)
        ? search.proses
        : undefined,
    page: pageParam(search.page),
  }),
})

/* ── Production module: detail routes ───────────────────────────────────────── */

/** Emir detail — opened by row link from the process board and the emir list. */
const emirDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/uretim/$emirNo',
  component: EmirDetailScreen,
})

/* ── End of Production module ───────────────────────────────────────────────── */

const modelsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/modeller',
  component: ModelsScreen,
  validateSearch: (search: Record<string, unknown>): ModelsSearch => ({
    query: textParam(search.query),
    mode: enumParam(search.mode, MODEL_MODES),
    sort: enumParam(search.sort, MODEL_SORTS),
    page: pageParam(search.page),
  }),
})

/* ── Models module: detail routes ────────────────────────────────────────────── */

/** /modeller/$modelKod — model card, colour variants and per-process BOM. */
const modelDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/modeller/$modelKod',
  component: ModelDetailScreen,
})

/**
 * /modeller/$modelKod/saya — digitizing the model's upper (saya) patterns.
 *
 * A child of the model card by address on purpose and NOT a sidebar entry: a
 * saya set belongs to exactly one model, and entering through the model card is
 * what makes attaching a scan to the wrong model impossible. It is session-
 * gated only, like the rest of modeller — every account may digitize; there is
 * no approval workflow, only calibration asks for an admin.
 */
const modelSayaRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/modeller/$modelKod/saya',
  component: SayaScreen,
  validateSearch: (search: Record<string, unknown>): SayaSearch => ({
    source: enumParam(search.source, SAYA_SOURCES),
  }),
})

/* ── End of Models module ────────────────────────────────────────────────────── */

/** Unknown addresses stay inside the shell too (404 card, sidebar stays put). */
const notFoundRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '$',
  component: NotFound,
})

const routeTree = rootRoute.addChildren([
  loginRoute,
  protectedRoute.addChildren([
    dashboardRoute,
    stockRoute,
    stockCardRoute,
    ordersRoute,
    orderDetailRoute,
    purchasingRoute,
    purchasingDetailRoute,
    cariRoute,
    cariDetailRoute,
    finansRoute,
    finansTahsilatRoute,
    productionRoute,
    modelsRoute,
    emirDetailRoute,
    modelDetailRoute,
    modelSayaRoute,
    notFoundRoute,
  ]),
])

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

function NotFound() {
  return (
    <div className="mx-auto max-w-md py-16 text-center">
      <p className="eyebrow">404</p>
      <h2 className="mt-1 font-display text-2xl font-semibold tracking-tight text-ink">
        Sayfa bulunamadı
      </h2>
      <p className="mt-1 text-data text-ink-muted">
        Adres değişmiş ya da bu ekran henüz eklenmemiş olabilir.
      </p>
      <Button asChild variant="primary" className="mt-4">
        <Link to="/">Panele dön</Link>
      </Button>
    </div>
  )
}
